import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TableSortLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';

function DocsAdmin() {
  const { t } = useAdminLanguage();
  const [docs, setDocs] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [formData, setFormData] = useState({ 
    title: '',
    file: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchDocs();
  }, [language]);

  useEffect(() => {
    handleSearch(searchQuery);
  }, [docs, searchQuery]);

  const fetchDocs = async () => {
    try {
      const response = await fetch(`/api/zonaldocs?lang=${language}`);
      const data = await response.json();
      setDocs(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des documents');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, file }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title.trim()) {
        setError('Le titre est obligatoire');
        return;
      }

      if (!isEditing && !formData.file) {
        setError('Le fichier est obligatoire');
        return;
      }

      if (formData.file) {
        const ext = formData.file.name.split('.').pop().toLowerCase();
        if (!['pdf', 'docx', 'doc'].includes(ext)) {
          setError('Seuls les fichiers PDF et DOCX sont acceptés');
          return;
        }
      }

      setLoading(true);
      setError('');
      setSuccess('');

      const fd = new FormData();
      fd.append('title', formData.title.trim());
      fd.append('lang', language);
      if (formData.file) {
        fd.append('file', formData.file);
      }

      if (isEditing) {
        const response = await fetch(`/api/zonaldocs/${editingId}`, {
          method: 'PUT',
          body: fd,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.message || 'Erreur lors de la mise à jour');
        }

        setSuccess('Document mis à jour avec succès');
      } else {
        const response = await fetch('/api/zonaldocs', {
          method: 'POST',
          body: fd,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.message || 'Erreur lors de l\'ajout');
        }

        setSuccess('Document ajouté avec succès');
      }

      setFormData({ title: '', file: null });
      setIsEditing(false);
      setEditingId(null);
      fetchDocs();
    } catch (err) {
      setError(err?.message || 'Erreur lors de l\'enregistrement du document');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (doc) => {
    setIsEditing(true);
    setEditingId(doc.id);
    setFormData({
      title: doc.title,
      file: null
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Êtes-vous sûr de vouloir supprimer ce document ?') || 'Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await fetch(`/api/zonaldocs/${id}?lang=${language}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      setSuccess('Document supprimé avec succès');
      fetchDocs();
    } catch (err) {
      setError('Erreur lors de la suppression du document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      window.open(`/api/zonaldocs/download/${id}?lang=${language}`, '_blank');
    } catch (err) {
      setError('Erreur lors du téléchargement');
    }
  };

  const handleSearch = (query) => {
    const lowerQuery = query.toLowerCase();
    const filtered = docs.filter(doc => 
      doc.title.toLowerCase().includes(lowerQuery)
    );
    setFilteredDocs(filtered);
  };

  const columns = useMemo(() => ([
    { id: 'title', label: t('Titre'), sortable: true },
    { id: 'extension', label: t('Type'), sortable: true },
  ]), [t]);

  const getComparableValue = (doc, property) => {
    switch (property) {
      case 'title':
      case 'extension':
        return doc[property]?.toString().toLowerCase() || '';
      default:
        return doc[property];
    }
  };

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(filteredDocs, {
    defaultOrderBy: 'title',
    getComparableValue,
  });

  const renderCellContent = (doc, columnId) => {
    switch (columnId) {
      case 'extension':
        const isDocx = doc.extension === 'docx' || doc.extension === 'doc';
        return (
          <Chip 
            icon={isDocx ? <DescriptionIcon /> : <PictureAsPdfIcon />}
            label={doc.extension.toUpperCase()} 
            color={isDocx ? 'primary' : 'error'}
            size="small" 
          />
        );
      default:
        return doc[columnId];
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des documents zonaux')}
          </Typography>

          <Info type="zonaldocs" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le document' : 'Ajouter un document')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  name="title"
                  label={t('Titre')}
                  value={formData.title}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  disabled={loading}
                  sx={{ height: '56px' }}
                >
                  {formData.file ? formData.file.name : t(isEditing ? 'Changer le fichier (optionnel)' : 'Choisir un fichier')}
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ title: '', file: null });
                    }}
                    sx={{ ml: 2 }}
                  >
                    {t('Annuler')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des documents')}
            </Typography>
            <TextField
              fullWidth
              label={t('Rechercher un document')}
              placeholder={t('Titre du document')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              autoComplete="off"
              sx={{ mb: 2 }}
            />

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((doc) => (
                    <TableRow key={doc.id}>
                      {columns.map((column) => (
                        <TableCell key={`${doc.id}-${column.id}`}>
                          {renderCellContent(doc, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(doc.id)}
                            color="primary"
                            disabled={loading}
                            title={t('Télécharger')}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(doc)}
                            color="primary"
                            disabled={loading}
                            title={t('Modifier')}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(doc.id)}
                            color="error"
                            disabled={loading}
                            title={t('Supprimer')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default DocsAdmin;

